<template>
  <div class="w-full max-w-xl py-20 bg-white flex flex-col space-y-20">
    <button class="flex items-center cursor-pointer" @click="$router.back()">
      <i aria-hidden="true" class="fas fa-chevron-left text-sm" />
      <p class="mx-6">
        {{ $t('rewards.automatic_rewards.confirmation.edit_selection') }}
      </p>
    </button>

    <h2>
      {{ $t('rewards.automatic_rewards.confirmation.title') }}
    </h2>

    <Card class="p-30 space-y-20">
      <div class="py-12 px-5 m-auto">
        <div class="flex items-center">
          <div class="flex items-center">
            <img
              :src="reward.imageUrl"
              aria-hidden="true"
              height="43px"
              width="72px"
              :alt="reward.name"
            />
            <div class="mx-10">
              <h6 class="-mb-4">
                {{
                  $t('rewards.automatic_rewards.confirmation.gift_card', [
                    reward.name,
                  ])
                }}
              </h6>
              <p class="text-sm">
                {{
                  $t(`rewards.automatic_rewards.confirmation.${reward.deliveryForm}`)
                }}
              </p>
              <button
                class="text-blue-500 underline font-semibold text-sm"
                @click="showDescription = !showDescription"
              >
                {{
                  $t(
                    `rewards.item_basket.${
                      showDescription ? 'hide' : 'view'
                    }_description`
                  )
                }}
              </button>
            </div>
          </div>
        </div>
        <div
          v-if="showDescription"
          class="mt-10 description"
          v-html="reward.description"
        />
      </div>

      <div class="mt-20 space-y-20">
        <p class="font-semibold text-base border-t pt-20">
          {{ $t('rewards.reward_confirmation.sending_to') }}
        </p>

        <div class="space-y-20" :class="{ 'border-b pb-20': usesPoints }">
          <div v-if="containsDigitalGiftCard">
            <div>
              <fieldset>
                <legend class="font-semibold">
                  {{ $t('rewards.reward_confirmation.email') }}
                </legend>
                <FormRadioSelect
                  v-if="emailOptions.length > 0"
                  name="email"
                  v-model="emailCode"
                  :data="emailOptions"
                />
                <NoContactOnFile v-else />
              </fieldset>
            </div>
            <div>
              <button
                class="text-blue-500 underline font-semibold text-sm ml-10"
                @click="$emit('addEmail')"
              >
                {{ $t('rewards.send_to_different_email') }}
              </button>
            </div>
          </div>

          <p
            v-if="containsDigitalGiftCard"
            class="font-semibold text-base border-b pb-10"
          ></p>

          <div v-if="containsPhysicalGiftCard">
            <div>
              <fieldset>
                <legend class="font-semibold">
                  {{ $t('rewards.reward_confirmation.address') }}
                </legend>
                <FormRadioSelect
                  v-if="addressOptions.length > 0"
                  name="address"
                  v-model="addressCode"
                  :data="addressOptions"
                />
                <NoContactOnFile v-else />
              </fieldset>
            </div>
            <div>
              <button
                class="text-blue-500 underline font-semibold text-sm ml-10"
                @click="$emit('addAddress')"
              >
                {{ $t('rewards.send_to_different_address') }}
              </button>
            </div>
          </div>

          <p
            v-if="containsPhysicalGiftCard"
            class="font-semibold text-base border-b pb-10"
          ></p>

          <div v-if="supportClientNumber" class="text-grey-500 text-sm">
            {{
              $t('common.contact_health_plan_csr.contact_info_change', [
                supportClientNumber,
              ])
            }}
          </div>
        </div>
      </div>
    </Card>

    <div class="mx-auto flex-shrink-0 space-y-20 flex flex-col justify-center">
      <HMButton class=" mt-20 w-full" :busy="loading" @click="save">
        {{ $t('rewards.automatic_rewards.confirmation.confirm') }}
      </HMButton>

      <HMButton
        class="w-full mt-20 self-center border border-grey-300"
        color="white"
        @click="$router.push('/rewards')"
      >
        {{ $t('common.cancel_btn') }}
      </HMButton>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import _isMatch from 'lodash/isMatch'
import { Card } from '@components/common/card'
import supportMixin from '@components/mixins/support'
import FormRadioSelect from '@components/common/forms/FormRadioSelect'
import NoContactOnFile from '@components/views/rewards/delivery-destination/NoContactOnFile'

export default {
  components: {
    Card,
    FormRadioSelect,
    NoContactOnFile,
  },
  mixins: [supportMixin],
  data() {
    return {
      addressCode: null,
      emailCode: null,
      editInfoModal: false,
      showDescription: false,
    }
  },
  computed: {
    ...mapGetters('rewards', {
      autoRewardDelivery: 'autoRewardDelivery',
      loading: 'setupAutoRewardLoading',
      reward: 'selectedAutoReward',
      usesPoints: 'usesPoints',
    }),
    ...mapGetters('member', ['addresses', 'emails', 'memberInfo']),
    addressOptions() {
      return this.addresses.map((x) => ({
        code: x.id.toString(),
        label: x.title,
        model: x,
      }))
    },
    emailOptions() {
      return this.emails.map((x) => ({
        code: x.id.toString(),
        label: x.address,
        model: x,
      }))
    },
    containsPhysicalGiftCard() {
      return _isMatch(this.reward, { deliveryForm: 'physical' })
    },
    containsDigitalGiftCard() {
      return _isMatch(this.reward, { deliveryForm: 'digital' })
    },
  },
  created() {
    if (!this.reward.id) {
      this.$router.push('/rewards')
    }
  },
  mounted() {
    this.addressCode =
      this.autoRewardDelivery?.addressId?.toString() ||
      this.addressOptions[0]?.code

    this.emailCode =
      this.autoRewardDelivery?.emailId?.toString() || this.emailOptions[0]?.code
  },
  methods: {
    ...mapActions('rewards', ['openConfirmAutoReward', 'setupAutoReward']),
    save() {
      const deliveryForm = this.reward.deliveryForm

      let shippingId = null
      switch (deliveryForm) {
        case 'digital':
          shippingId = this.emailOptions.find((x) => x.code === this.emailCode)
            ?.model?.id
          break
        case 'physical':
          shippingId = this.addressOptions.find(
            (x) => x.code === this.addressCode
          )?.model?.id
          break
      }

      this.setupAutoReward({
        incentiveProductId: this.reward.productId,
        deliveryForm,
        shippingId,
      }).then(() => {
        this.openConfirmAutoReward()
        this.$router.push('/rewards')
      })
    },
  },
}
</script>

<style>
.description > ul {
  list-style: disc;
  padding-left: 40px;
}
</style>